import { WebAnalyticsEventName } from 'hydra-data-types';
import * as icons from '../../components/assets/images/Icons';
import { DropdownSelectScreenLayoutConfig } from '../../components/flows/DropdownSelectScreen';
import { getPaymentPathRoute } from '../../util/ConditionalNextScreenSelectors';
import { DIRECT_MEDICARE_ROUTE, getDirectMedicareFlowRoute } from '../../util/MedicareAccess';
import {
  GAD7ScreenName,
  MedicareAccessScreenName,
  PHQ9ScreenName,
  ScreenConfig,
  ScreenType,
  SharedScreenName,
  Us65PlusScreenName,
} from '../FlowTypes';
import {
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
  HEALTH_COVER_OTHER,
  MEDICARE_ADVANTAGE_PROVIDERS,
} from '../InsuranceProviders';
import { US_STATES } from '../UsStates';
import { NEXT_BUTTON_TITLE, NervaControlScreenName } from './NervaControlScreenNames';

const GAD7_PHQ9_BODY =
  'Over the last 2 weeks, how often have you been bothered by the following problems?';

const GAD7_PHQ9_CHOICES = [
  { label: 'Not at all', value: '0' },
  { label: 'Several days', value: '1' },
  { label: 'More than half the days', value: '2' },
  { label: 'Nearly every day', value: '3' },
];

/**
 * Shared layout for the "Do you have health insurance coverage?" (Medicare type) question. Used by
 * the referred health-cover screen and the D2C pre-sign-up screen so the content lives in one place;
 * only the routing differs between the two.
 */
export const HEALTH_COVER_LAYOUT = {
  body: ['Do you have health insurance coverage?'],
  choices: [HEALTH_COVER_MEDICARE_ORIGINAL, HEALTH_COVER_MEDICARE_ADVANTAGE, HEALTH_COVER_OTHER],
};

/**
 * Shared layout for the state-of-residence question that follows every Original Medicare answer.
 * Only the routing differs between configs.
 */
export const MEDICARE_ACCESS_STATE_LAYOUT: DropdownSelectScreenLayoutConfig = {
  question: 'Which state do you live in?',
  placeholder: 'Select your state',
  options: US_STATES,
  primaryButtonTitle: NEXT_BUTTON_TITLE,
};

/** Medicare Access flow screens — shared across referred Nerva flow configs. */
export const MEDICARE_ACCESS_SCREENS: Array<
  ScreenConfig<
    | MedicareAccessScreenName
    | GAD7ScreenName
    | PHQ9ScreenName
    | SharedScreenName.Auth
    | SharedScreenName.DownloadApp
    | SharedScreenName.AlreadyPaidDownloadApp
    | SharedScreenName.Payment
    | NervaControlScreenName
    | Us65PlusScreenName
  >
> = [
  {
    screenName: GAD7ScreenName.Gad7Nervous,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Feeling nervous, anxious or on edge'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7StopWorrying,
  },
  {
    screenName: GAD7ScreenName.Gad7StopWorrying,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Not being able to stop or control worrying'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7WorryTooMuch,
  },
  {
    screenName: GAD7ScreenName.Gad7WorryTooMuch,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Worrying too much about different things'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7TroubleRelaxing,
  },
  {
    screenName: GAD7ScreenName.Gad7TroubleRelaxing,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Trouble relaxing'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7Restless,
  },
  {
    screenName: GAD7ScreenName.Gad7Restless,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Being so restless that it is hard to sit still'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7Annoyed,
  },
  {
    screenName: GAD7ScreenName.Gad7Annoyed,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Becoming easily annoyed or irritable'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: GAD7ScreenName.Gad7Afraid,
  },
  {
    screenName: GAD7ScreenName.Gad7Afraid,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Feeling afraid as if something awful might happen'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9LittleInterest,
  },
  {
    screenName: PHQ9ScreenName.Phq9LittleInterest,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Little interest or pleasure in doing things'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9FeelingDown,
  },
  {
    screenName: PHQ9ScreenName.Phq9FeelingDown,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Feeling down, depressed or hopeless'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9TroubleSleeping,
  },
  {
    screenName: PHQ9ScreenName.Phq9TroubleSleeping,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Trouble falling asleep, staying asleep, or sleeping too much'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9FeelingTired,
  },
  {
    screenName: PHQ9ScreenName.Phq9FeelingTired,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Feeling tired or having little energy'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9PoorAppetite,
  },
  {
    screenName: PHQ9ScreenName.Phq9PoorAppetite,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Poor appetite or overeating'],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9FeelingBad,
  },
  {
    screenName: PHQ9ScreenName.Phq9FeelingBad,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [
        GAD7_PHQ9_BODY,
        'Feeling bad about yourself — or that you’re a failure or have let yourself or your family down',
      ],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9TroubleConcentrating,
  },
  {
    screenName: PHQ9ScreenName.Phq9TroubleConcentrating,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [
        GAD7_PHQ9_BODY,
        'Trouble concentrating on things, such as reading the newspaper or watching television',
      ],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9SlowOrFast,
  },
  {
    screenName: PHQ9ScreenName.Phq9SlowOrFast,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [
        GAD7_PHQ9_BODY,
        'Moving or speaking so slowly that other people could have noticed. Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
      ],
      choices: GAD7_PHQ9_CHOICES,
    },
    nextScreen: PHQ9ScreenName.Phq9SelfHarm,
  },
  {
    screenName: PHQ9ScreenName.Phq9SelfHarm,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [
        GAD7_PHQ9_BODY,
        'Thoughts that you would be better off dead or of hurting yourself in some way',
      ],
      choices: GAD7_PHQ9_CHOICES,
    },
    // 0 ("not at all") continues screening; any positive score (1/2/3) triages through the
    // suicide-ideation check first — the confirmation step must never be skipped (safety-critical).
    nextScreen: {
      '0': PHQ9ScreenName.Phq9HarmOthers,
      fallback: MedicareAccessScreenName.MedicareAccessSuicideCheck,
    },
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessSuicideCheck,
    type: ScreenType.MedicareAccessSafetyCheck,
    layout: {
      body: [
        'You reported thoughts of being better off dead or of hurting yourself. Do you have thoughts of suicide?',
      ],
      choices: ['Yes', 'No'],
    },
    // A positive answer (or any unexpected value) escalates to the crisis screen; No continues screening.
    nextScreen: {
      Yes: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
      No: PHQ9ScreenName.Phq9HarmOthers,
      fallback: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
    },
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
    type: ScreenType.MedicareAccessHelplines,
    layout: {},
    // An escalated safety-check answer is final — the user cannot go back and change it.
    resetHistoryOnEntry: true,
    nextScreen: MedicareAccessScreenName.MedicareAccessExclusion,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessExclusion,
    type: ScreenType.MultiSelect,
    // First screen after the PROMs — completed PROMs answers cannot be changed.
    resetHistoryOnEntry: true,
    layout: {
      body: ['Do any of the following apply to you right now? Select all that apply.'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        { label: 'Diagnosed with dementia', value: 'dementia' },
        { label: 'Have memory loss affecting daily tasks', value: 'memoryLoss' },
        {
          label:
            'Diagnosed with Schizophrenia, schizoaffective disorder, other psychotic disorders',
          value: 'psychoticDisorder',
        },
        { label: 'Experienced psychosis in the past year', value: 'recentPsychosis' },
        { label: 'Have an active untreated eating disorder', value: 'eatingDisorder' },
        { label: 'Currently pregnant', value: 'pregnant' },
        { label: 'Currently receiving hospice or palliative care', value: 'hospiceOrPalliative' },
        {
          label: 'Currently enrolled in a community behavioral health clinic (CCBHC) program',
          value: 'ccbhc',
        },
        {
          label: 'Currently live in a nursing home or long-term care facility',
          value: 'nursingHome',
        },
        { label: 'None of the above', value: 'noneOfTheAbove' },
      ],
    },
    // Once the initial check resolves as eligible_pending_diagnosis, eligibility is rechecked with
    // the completed PROMs and exclusion answer.
    nextScreen: MedicareAccessScreenName.MedicareAccessCoverageLoading,
  },
  {
    // Waits for the eligibility result on the track doc, then routes: eligible →
    // OriginalQualified (its nextScreen), rejected → the comparison-group Control screen.
    screenName: MedicareAccessScreenName.MedicareAccessCoverageLoading,
    type: ScreenType.MedicareAccessCoverageLoading,
    resetHistoryOnEntry: true,
    layout: {
      title: 'Checking your coverage',
      subtitle: 'This will only take a moment.',
      showAiBadge: false,
      loadingBars: [
        {
          title: 'Saving your responses',
          icon: icons.document,
          startNumber: 0,
          endNumber: 100,
          durationMs: 4000,
        },
        {
          title: 'Checking your Medicare coverage',
          icon: icons.heartbeat,
          startNumber: 0,
          endNumber: 100,
          durationMs: 26000,
        },
        {
          title: 'Confirming your eligibility',
          icon: icons.tickOutline,
          startNumber: 0,
          endNumber: 100,
          durationMs: 30000,
        },
      ],
      footnoteHeader: 'Confirming eligibility',
    },
    rejectedScreen: MedicareAccessScreenName.MedicareAccessControl,
    // Direct (D2C) flow only: clinically-disqualified users get the 20% outcome screen instead of
    // the comparison-group screen.
    disqualifiedScreen: MedicareAccessScreenName.MedicareAccessInsurerNotCovered,
    nextScreen: MedicareAccessScreenName.MedicareAccessConsent,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessHealthCover,
    type: ScreenType.SingleSelect,
    layout: HEALTH_COVER_LAYOUT,
    // Asked after Program Overview (US 65+). getPaymentPathRoute reads this answer: Original asks the
    // state (which gates the CMS disclosure and Medicare details); Advantage and Other both collect
    // the insurance provider (Other isn't in CMS Access, so it follows the same non-Original path).
    nextScreen: {
      medicareOriginal: MedicareAccessScreenName.MedicareAccessState,
      medicareAdvantage: MedicareAccessScreenName.MedicareAccessAdvantageProvider,
      medicareOther: MedicareAccessScreenName.MedicareAccessAdvantageProvider,
      fallback: SharedScreenName.DownloadApp,
    },
    nextScreenSelector: getPaymentPathRoute,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessAdvantageProvider,
    type: ScreenType.InsuranceProvider,
    layout: {
      question: 'Select your insurance provider',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      providers: MEDICARE_ADVANTAGE_PROVIDERS,
    },
    nextScreen: MedicareAccessScreenName.MedicareAccessAdvantageQualified,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessAdvantageQualified,
    type: ScreenType.Presentation,
    layout: {
      title: 'You qualify for Nerva at no cost.',
      content: [
        {
          type: 'text',
          content: "There's nothing to pay, and no automatic charges for the program.",
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: Us65PlusScreenName.Us65PlusCoverageLoading,
  },
  {
    screenName: Us65PlusScreenName.Us65PlusCoverageLoading,
    type: ScreenType.Us65PlusGrantAccess,
    showHeaderBar: false,
    // D2C grant users hit the "already have access" download screen, which omits the trial-expiry
    // copy the D2C (paid) download screen would otherwise show. Referred users keep their own
    // download screen unchanged.
    nextScreen: {
      [DIRECT_MEDICARE_ROUTE]: SharedScreenName.AlreadyPaidDownloadApp,
      fallback: SharedScreenName.DownloadApp,
    },
    nextScreenSelector: getDirectMedicareFlowRoute,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessMBI,
    type: ScreenType.MedicareAccessMbi,
    layout: {
      title: "First, let's confirm a few details",
      subtitle: "We'll use this information to confirm your Medicare eligibility.",
      medicareSectionTitle: 'Medicare details',
      personalSectionTitle: 'Personal details',
      mbiLabel: 'Medicare Beneficiary Identifier',
      mbiPlaceholder: 'Enter your 11-character MBI',
      mbiHelperText: '11 characters from your Medicare card (e.g. 1EG4-TE5-MK72).',
      mbiTooltip:
        'Your MBI is the 11-character identifier printed on your red, white and blue Medicare card.',
      givenNameLabel: 'Legal given name (including middle name)',
      familyNameLabel: 'Legal family name',
      addressLabel: 'Residential address',
      addressHelperText: 'Include street number and name, suburb, state and zip code.',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      findingYourDetails: {
        title: 'Finding your details',
        imageAlt: 'Sample Medicare card showing where to find your MBI and legal name',
      },
    },
    // Reached only by Medicare Original (US 65+) from the payment-path decision; starts PROMs.
    nextScreen: GAD7ScreenName.Gad7Nervous,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessExpectationSetting,
    type: ScreenType.ExpectationSetting,
    layout: {
      title: 'What to expect',
      intro:
        "Nerva works best as a partnership – we bring the program and the science, and we're alongside you the whole way.",
      promise: {
        heading: 'What we promise you:',
        items: [
          'Guide you through every step of the 6-week program.',
          'Backed by real science from Monash University.',
          'Keep your care team in the loop.',
        ],
      },
      ask: {
        heading: "What we'll ask of you:",
        items: [
          'A few minutes a day for your guided session.',
          'Your check-ins along the way (start, 6 weeks, then every 3 months).',
        ],
      },
      closing:
        "We've seen this work for thousands of people. We'd love to help it work for you, too.",
      namePrompt: 'Type your name to begin:',
      namePlaceholder: 'Your name',
      primaryButtonTitle: "I'm in — start my program",
    },
    nextScreen: Us65PlusScreenName.Us65PlusCoverageLoading,
  },
  {
    // Safety screening shown when the self-harm question scores 0. Not a scored PHQ-9 item.
    screenName: PHQ9ScreenName.Phq9HarmOthers,
    type: ScreenType.NumberedSingleSelect,
    layout: {
      body: [GAD7_PHQ9_BODY, 'Considered hurting someone else in some way'],
      choices: GAD7_PHQ9_CHOICES,
    },
    // 0 continues screening; any positive answer confirms before escalating (mis-tap guard).
    nextScreen: {
      '0': MedicareAccessScreenName.MedicareAccessExclusion,
      fallback: MedicareAccessScreenName.MedicareAccessHarmOthersCheck,
    },
  },
  {
    // Confirmation guard: a positive harm-to-others answer routes here before the crisis screen.
    screenName: MedicareAccessScreenName.MedicareAccessHarmOthersCheck,
    type: ScreenType.MedicareAccessSafetyCheck,
    layout: {
      body: [
        'You reported considering hurting someone else in some way. Are you sure this is your answer?',
      ],
      choices: ['Yes', 'No'],
    },
    // Yes (or any unexpected value) escalates to the crisis screen; No treats it as retracted.
    nextScreen: {
      Yes: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
      No: MedicareAccessScreenName.MedicareAccessExclusion,
      fallback: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
    },
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessConsent,
    type: ScreenType.MultiSelect,
    layout: {
      bodyAlign: 'left',
      heading:
        "Mindset Health Inc.'s services are part of the CMS ACCESS Model. By joining, you acknowledge:",
      body: [
        '• Joining is voluntary, and you can change your mind.',
        '• Nerva will be your only provider in the program for this condition at a time.',
        '• You can leave any time after your first 90 days.',
        "• There's no cost to you to take part.",
        '• To support your care, your Medicare information may be shared with us — protected under privacy laws including HIPAA.',
      ],
      choices: ['I agree to join the program with Nerva for my care.'],
      primaryButtonTitle: 'Agree & continue',
    },
    nextScreen: MedicareAccessScreenName.MedicareAccessOriginalQualified,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessDisclosure,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            "Mindset Health Inc., the company behind Nerva, is taking part in the CMS ACCESS Model — a new Medicare payment model from the Centers for Medicare & Medicaid Services that's still being evaluated.",
        },
        {
          type: 'text',
          content: "Here's what you should know:",
        },
        {
          type: 'bulletItem',
          content:
            'We may share your information with CMS, and CMS may securely access your health records — all protected by federal privacy rules.',
        },
        {
          type: 'bulletItem',
          content:
            "Not everyone who's eligible joins right away — some people are placed at random into a comparison group. Either way, your Medicare benefits, rights, and coverage stay exactly the same.",
        },
      ],
      primaryButtonTitle: 'I understand and continue.',
    },
    nextScreen: MedicareAccessScreenName.MedicareAccessMBI,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessControl,
    type: ScreenType.Presentation,
    layout: {
      title: 'You are not eligible to enroll through ACCESS.',
      content: [
        {
          type: 'text',
          content:
            'These services are part of a federal payment model test being conducted by the Centers for Medicare & Medicaid Services (CMS). To evaluate the model, some individuals are assigned to a comparison group and are not eligible to enroll for 12 months. This is a random assignment made by CMS for evaluation purposes only and is not based on anything about you or your health. You may continue to receive other services as usual and your Medicare coverage, benefits, and rights are not affected in any way. If you have questions, you may call 1-800-MEDICARE (1-800-633-4227). Teletypewriter (TTY) users call 1-877-486-2048.',
        },
        {
          type: 'text',
          content:
            'You can still use Nerva outside the CMS ACCESS Model by purchasing it directly. Continuing will show you the available paid option.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    // Comparison-group users cannot receive ACCESS services or a free ACCESS entitlement, but can
    // purchase Nerva through the normal trial checkout with any attached referral coupon intact.
    nextScreen: SharedScreenName.Payment,
  },
  {
    // Direct (D2C) outcome for not-covered or clinically-disqualified users: a 20% discount is
    // applied at the payment page (US 65+), so this screen just frames it before checkout.
    screenName: MedicareAccessScreenName.MedicareAccessInsurerNotCovered,
    type: ScreenType.Presentation,
    layout: {
      title: 'Your Coverage Results',
      content: [
        {
          type: 'text',
          content:
            "While your insurer doesn't cover Nerva directly, we've applied a 20% discount to make the program more accessible for you.",
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    // Every D2C user who lands here is an unsuccessful CMS-Access outcome — not-Original or
    // clinically-disqualified — so report it as they view their coverage result.
    viewEvent: { event: WebAnalyticsEventName.o_insurance_outcome, outcome: 'unsuccessful' },
    nextScreen: SharedScreenName.Payment,
  },
  {
    screenName: MedicareAccessScreenName.MedicareAccessOriginalQualified,
    type: ScreenType.Presentation,
    layout: {
      title: 'You qualify for Nerva at no cost.',
      content: [
        {
          type: 'text',
          content: "There's nothing to pay, and no automatic charges for the program.",
        },
        {
          type: 'text',
          content:
            "Before you begin, we'll quickly walk you through how the program works, what support you'll receive, and what we'll ask from you along the way.",
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: MedicareAccessScreenName.MedicareAccessExpectationSetting,
  },
];
